import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlaybackTypes } from 'models/kitchenSink';
import { VideoCtvRedux, KitcenSinkRowItemDataItem, KitchenSinkRowItem, RelatedItem, PregameVideo } from 'store-types';
import { ChannelKey, Provider, SyncbakPlaybackResponse } from 'app-types';

import { getPlaybackUrl, clearPlaybackUrl } from 'actions/playback';
import {
  constructGAMDAIResource,
  constructFWDAIResource,
  createNonDAIContentResource,
  createSyncbakResource,
  createDAIAPIContentResource,
  ResourceConfig,
} from 'support/player/resourceHelper';
import {
  getVideoMetadata,
  getPregameMetadata,
  getCarouselMetadata,
  getClickedVideoMetadata,
} from 'support/analytics/analyticsHelper';
import Tracker from 'support/analytics/Tracker';
import { useNetworkName, useFW } from 'hooks/kitchenSink';

interface PlayerResourceProps {
  render?: (props: { resource?: ResourceConfig; onError?: (error: unknown) => void; loading: boolean }) => React.ReactElement | null;
  onError?: (error: unknown) => void;
  videoToPlay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  videoToDisplay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  row?: KitchenSinkRowItem;
  isAutoplay: boolean;
  authorizedProvider?: Provider;
  isCarousel?: boolean;
  slideNum?: number | null;
}

type UsePlaybackResourceParams = {
  videoToPlay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  videoToDisplay?: KitcenSinkRowItemDataItem | RelatedItem | PregameVideo;
  row?: KitchenSinkRowItem;
  isAutoplay: boolean;
  authorizedProvider?: Provider;
  onError: (error: unknown) => void;
  isCarousel: boolean;
  slideNum?: number | null;
};

const usePlaybackResource = ({
  videoToPlay,
  videoToDisplay,
  row: providedRow,
  isAutoplay,
  authorizedProvider,
  onError: providedOnError,
  isCarousel,
  slideNum,
}: UsePlaybackResourceParams) => {
  const dispatch = useDispatch();
  const [resource, setResource] = useState<ResourceConfig | undefined>();
  const networkName = useNetworkName(videoToPlay?.network);
  const isFreeWheel = useFW();

  const csidLive = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink.data?.config?.modules?.Freewheel?.params?.csid_live,
  );

  const csidVod = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink.data?.config?.modules?.Freewheel?.params?.csid_vod,
  );

  const contextFmsParams = useSelector((state: VideoCtvRedux) => state.fms?.data?.fms_params);
  const dmaStation = videoToPlay?.dmaStation;
  const hasFmsParamsOrStation = contextFmsParams || dmaStation;
  const fmsParams = hasFmsParamsOrStation
    ? {
        ...(contextFmsParams || {}),
        ...(dmaStation ? { imafw_station: dmaStation } : {}),
      }
    : undefined;

  // Storing in a ref because we don't actually want video object changes to trigger
  // a resource change.
  const videoContext = {
    video: videoToPlay,
    videoNetworkName: networkName,
    displayedVideo: videoToDisplay,
    row: providedRow,
    onError: providedOnError,
    currentProvider: authorizedProvider,
    tracking: {
      autoplay: isAutoplay,
      get preGame() {
        if (videoToDisplay && videoToPlay) {
          const pregame = videoToDisplay.event?.pregame;
          const preEventStatus = videoToDisplay.event?.status;
          const displayedVideoId = videoToDisplay.id;
          const playingVideoId = videoToPlay.id;
          const preGameIsPlaying =
            pregame?.trackingId === videoToPlay.trackingId;

          const displayedVideoIsPlaying = displayedVideoId === playingVideoId;

          if (preGameIsPlaying || (displayedVideoIsPlaying && pregame)) {
            return {
              // This is only for analytics
              eventId: videoToDisplay.trackingId,
              // Need the following escape hatch to set active since the player
              // may start the active video before preEventStatus is updated
              eventStatus: displayedVideoIsPlaying ? 'active' : preEventStatus,
            };
          }
        }
        return {};
      },
    },
    freeWheel: {
      csidLive,
      csidVod,
      fmsParams,
      isFreeWheel,
    },
  };
  const videoContextRef = useRef(videoContext);
  useEffect(() => {
    videoContextRef.current = videoContext;
  });

  const lastValidVideoInfo = useRef<Record<string, string | null | undefined>>({
    videoId: null,
    rowId: null,
    displayedVideoId: null,
  });

  const videoId = videoToPlay?.id;
  const rowId = providedRow?.id;
  const displayedVideoId = videoToDisplay?.id;

  const isTrackingDataReady = useSelector(
    (state: VideoCtvRedux) => state.analytics?.tracking?.dataHasBeenSet,
  );

  const isFMSInitialized = useSelector((state: VideoCtvRedux) => state.fms?.initialized);

  const canConstructResource = !isFreeWheel || isFMSInitialized;

  const isCcEnabled = useSelector((state: VideoCtvRedux) => state.closedCaptioning);

  const clickedVideoId = useSelector((state: VideoCtvRedux) => state.clickedVideo?.videoId);
  const rowTitle = useSelector((state: VideoCtvRedux) => state.clickedVideo?.rowTitle ?? undefined);
  const rowPos = useSelector((state: VideoCtvRedux) => state.clickedVideo?.rowPos ?? undefined);
  const rowCol = useSelector((state: VideoCtvRedux) => state.clickedVideo?.rowCol ?? undefined);

  const isClicked = videoId === clickedVideoId;

  const ks = useSelector((state: VideoCtvRedux) => state.kitchenSink.data);
  const auth = useSelector((state: VideoCtvRedux) => state.auth);
  const geoInfo = useSelector((state: VideoCtvRedux) => state.geoInfo);

  const daiApiContentSourceId =
    ks.config?.modules?.prerollParams?.params?.contentSourceId;

  useEffect(() => {
    const id3OwnersSet = new Set<string>();

    if (ks.config?.modules?.ID3GameEndTransitions?.enabled) {
      const gameEndParams = ks.config?.modules?.gameEnd?.params;
      if (gameEndParams && typeof gameEndParams === 'object') {
        Object.keys(gameEndParams).forEach(key => id3OwnersSet.add(key));
      }
    }

    if (ks.config?.modules?.ID3StreamEndTransitions?.enabled) {
      const broadcastEndParams = ks.config?.modules?.broadcastEnd?.params;
      if (broadcastEndParams && typeof broadcastEndParams === 'object') {
        Object.keys(broadcastEndParams).forEach(key => id3OwnersSet.add(key));
      }
    }

    const id3Owners = Array.from(id3OwnersSet);

    if (!isTrackingDataReady || !canConstructResource) return;

    const fetchPlaybackUrl = (video: { playback: { isSyncbak: boolean }; network: ChannelKey } | null, provider?: Provider | null) => {
      return dispatch(getPlaybackUrl(video, provider));
    };

    const createAndSetResource = async () => {
      let res;
      let stationCode;
      const {
        video,
        videoNetworkName,
        displayedVideo,
        row,
        onError,
        tracking,
        currentProvider,
        freeWheel: {
          csidLive: csidLiveVal,
          csidVod: csidVodVal,
          fmsParams: fmsParamsVal,
          isFreeWheel: isFW,
        },
      } = videoContextRef.current;
      const isPlayingSameVideo = () => {
        const {
          videoId: lastValidVideoId,
          rowId: lastValidRowId,
          displayedVideoId: lastValidDisplayedVideoId,
        } = lastValidVideoInfo.current;

        return (
          lastValidVideoId === video?.id &&
          lastValidRowId === row?.slug &&
          lastValidDisplayedVideoId === displayedVideo?.id
        );
      };

      const vidObj = JSON.parse(JSON.stringify(video ?? null));

      if (vidObj) {
        if (isPlayingSameVideo()) return;

        if (vidObj.playback.type === PlaybackTypes.STATION_OPT_IN) {
          if (
            auth.paramountPlus.user.data !== null &&
            auth.adobe.authN !== null
          ) {
            // paramount plus logged in && adobe (mvpd) logged in
            // playbackMatrix -> both
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.both ===
              PlaybackTypes.SYNCBAK;
            vidObj.playback =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.both;
          } else if (auth.adobe.authN !== null) {
            // adobe (mvpd) logged in
            // playbackMatrix -> adobe
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.adobe ===
              PlaybackTypes.SYNCBAK;
            vidObj.playback =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix.adobe;
          } else if (auth.paramountPlus.user.data !== null) {
            // paramount plus logged in
            // playbackMatrix -> paramountplus
            vidObj.playback.isSyncbak =
              ks.playback[PlaybackTypes.STATION_OPT_IN].playbackMatrix
                .paramountplus === PlaybackTypes.SYNCBAK;
            vidObj.playback.type =
              ks.playback[
                PlaybackTypes.STATION_OPT_IN
              ].playbackMatrix.paramountplus;
          } else {
            // not logged in
            // syncbak
            vidObj.playback.isSyncbak = true;
            vidObj.playback.type = PlaybackTypes.SYNCBAK;
          }
        }

        if (vidObj.ads.isDai && !vidObj.playback.isSyncbak) {
          dispatch(clearPlaybackUrl());
          if (isFW) {
            res = await constructFWDAIResource(
              vidObj,
              fmsParamsVal,
              csidLiveVal,
              id3Owners,
            );
          } else {
            res = await constructGAMDAIResource(vidObj);
          }
        } else if (vidObj.playback.isSyncbak) {
          const response = await (fetchPlaybackUrl(vidObj, currentProvider) as unknown as Promise<SyncbakPlaybackResponse | null>);
          if (response && response.streamUrl && !response.errorCode) {
            const { streamUrl, stationCode: code } = response;
            res = createSyncbakResource(streamUrl, id3Owners);
            stationCode = code;
          } else {
            onError({ type: 'syncbak', code: response?.errorCode || null });
          }
        } else {
          // Non-DAI Content
          if (
            vidObj.properties &&
            vidObj.properties.type === 'vod' &&
            !!daiApiContentSourceId
          ) {
            res = await createDAIAPIContentResource(
              vidObj,
              daiApiContentSourceId,
              fmsParamsVal,
              csidVodVal,
            );
          } else {
            res = createNonDAIContentResource(vidObj, id3Owners);
          }
          dispatch(clearPlaybackUrl());
        }
      }

      // Attach analytics metadata and set resource
      if (res) {
        const {
          autoplay,
          preGame,
        } = tracking;
        const eventId = (preGame as { eventId?: string }).eventId;
        const eventStatus = (preGame as { eventStatus?: string }).eventStatus;

        const metadata = {
          ...getVideoMetadata(
            vidObj,
            displayedVideo,
            row,
            autoplay,
            stationCode,
            videoNetworkName,
            isCcEnabled,
            isCarousel,
            geoInfo,
          ),
          ...getPregameMetadata(eventId, eventStatus),
          ...Tracker.getHbParams(),
          ...getCarouselMetadata(isCarousel, slideNum as number),
          ...getClickedVideoMetadata(isClicked, {
            rowTitle,
            rowPos,
            rowCol,
          }),
        };
        if (res instanceof Array) {
          res.forEach(async individualResource => {
            const r = individualResource;
            r.metadata = {
              ...individualResource.metadata,
              ...metadata,
            };
          });
        } else {
          res.metadata = {
            ...res.metadata,
            ...metadata,
          };
        }

        setResource(res);

        lastValidVideoInfo.current = {
          videoId: video?.id,
          rowId: row?.slug,
          displayedVideoId: displayedVideo?.id,
        };
      }
    };

    createAndSetResource();
  }, [
    dispatch,
    videoId,
    displayedVideoId,
    rowId,
    isTrackingDataReady,
    canConstructResource,
    isCcEnabled,
    isCarousel,
    slideNum,
    isClicked,
    rowTitle,
    rowPos,
    rowCol,
    ks,
    auth,
    geoInfo,
    daiApiContentSourceId,
  ]);

  return [resource];
};

const PlayerResource = ({
  render = () => null,
  onError = () => null,
  videoToPlay,
  videoToDisplay,
  row,
  isAutoplay,
  authorizedProvider,
  isCarousel = false,
  slideNum = null,
}: PlayerResourceProps) => {
  const [resource] = usePlaybackResource({
    videoToPlay,
    videoToDisplay,
    row,
    isAutoplay,
    authorizedProvider,
    onError,
    isCarousel,
    slideNum,
  });
  const onResourceError = (error: unknown) => {
    onError({ type: 'resource', error });
  };
  const syncbakLoading = useSelector((state: VideoCtvRedux) => state.syncbak.loading);

  return render({
    resource,
    onError: onResourceError,
    loading: syncbakLoading,
  });
};

export default PlayerResource;
